import {
  statNumberByKey,
  type TradingStrategyDto,
} from "../../api/hooks/useTradingStrategies";
import type { ReferenceValue } from "../../api/hooks/useReference";
import { countStrategies } from "./strategySides";

/**
 * Everything the strategy list can be filtered by, in one place.
 *
 * The screen used to hold a `useMemo` per filter and a `searchParams` call per control, which meant
 * adding a filter touched the component in five places and the "how many are on" count was written
 * out by hand beside them. Here each filter states once how it reads a strategy, what it offers,
 * and how it survives a round trip through the URL. The badge, the option counts and the no-results
 * message are all derived from the same list, so none of them can drift from what is applied.
 *
 * The split that matters: a MULTI filter holds a list and means "any of these", because asking for
 * Breakout or Momentum is a real question. A BOUND filter holds one figure, because "over 50%/yr or
 * over 100%/yr" is not — the looser bound would simply win. A TEXT filter holds what was typed,
 * which is the one thing that cannot be offered as a set of options.
 */

export type MultiFilterKey =
  | "symbol"
  | "assetClass"
  | "riskProfile"
  | "direction"
  | "timeframe";

export type BoundFilterKey =
  | "minAvgAnnualProfit"
  | "minWinRate"
  | "maxDrawdown";

export type TextFilterKey = "name";

export type FilterKey = MultiFilterKey | BoundFilterKey | TextFilterKey;

export interface StrategyFilterState {
  multi: Record<MultiFilterKey, string[]>;
  bounds: Record<BoundFilterKey, number | null>;
  text: Record<TextFilterKey, string>;
}

/**
 * The reference lists, resolved once by the page and handed to these pure functions.
 *
 * Keyed by filter rather than by endpoint so a caller cannot pass the wrong list for a filter — a
 * mistake nothing else would catch, since 7 is a valid asset class and a valid timeframe.
 */
export type FilterReference = Partial<Record<MultiFilterKey, ReferenceValue[]>>;

/** The reference list a filter draws on, or undefined where its values are data rather than a set. */
export type FilterReferenceList =
  | "asset-classes"
  | "timeframes"
  | "risk-profiles"
  | "strategy-directions"
  | "data-feeds";

interface MultiFilterDefinition {
  key: MultiFilterKey;
  /**
   * Where the options come from. A reference-backed filter offers every value the package defines,
   * so a value does not quietly disappear the moment the last strategy using it is unpublished —
   * which is exactly what deriving them from the catalogue used to do.
   */
  reference?: FilterReferenceList;
  /** Values this strategy carries. Symbol is the only one that returns more than one. */
  valuesOf: (strategy: TradingStrategyDto, reference: ReferenceValue[]) => string[];
  /** The options to offer, already in the order they should be shown. */
  optionsOf: (
    strategies: TradingStrategyDto[],
    reference: ReferenceValue[],
  ) => string[];
  /**
   * False while the front office holds nothing to filter on. The control still renders — disabled,
   * saying why — rather than being hidden and re-laying the panel out when the data arrives.
   */
  available: boolean;
  /**
   * Selections show as tags inside the control, so it grows downwards as they are picked. Such a
   * filter gets a row to itself: sharing one leaves the others floating at the top of a row as tall
   * as however many the customer chose.
   */
  growsWithSelection?: boolean;
}

interface BoundFilterDefinition {
  key: BoundFilterKey;
  /** The published figure to compare, or null where this strategy does not carry it. */
  read: (strategy: TradingStrategyDto) => number | null;
  satisfies: (figure: number, bound: number) => boolean;
  /** The bounds offered, in the order shown. Fixed rather than derived: a bucket list that moves
   *  with the catalogue would change what a shared link means. */
  choices: readonly number[];
}

interface TextFilterDefinition {
  key: TextFilterKey;
  /** The text a typed term is matched against. */
  read: (strategy: TradingStrategyDto) => string;
}

/**
 * The filter carries the member name, not the id.
 *
 * It is the stable token, so a shared link still reads as "?timeframe=OneDay" rather than "=7", and it
 * survives anything except a rename, which the package's tests forbid.
 */
function nameOf(reference: ReferenceValue[], id: number): string {
  return reference.find((value) => value.id === id)?.name ?? "";
}

/** Every value the package defines, in the order it defines them - which is the display order. */
function namesOf(reference: ReferenceValue[]): string[] {
  return reference.map((value) => value.name);
}

function distinct(
  strategies: TradingStrategyDto[],
  valuesOf: (s: TradingStrategyDto) => string[],
): string[] {
  return [...new Set(strategies.flatMap(valuesOf))].filter((v) => v.length > 0);
}

// Options come from the reference endpoints, not from the values the catalogue happens to hold, so a
// filter offers a value even when no strategy currently carries it and stops offering one just because
// somebody typed it. Symbol is the exception: tickers are data, and there are too many to offer them
// all, so those still come from the catalogue.
const symbolsOf = (strategy: TradingStrategyDto): string[] => {
  const named = [strategy.chartedSymbol, strategy.bullSymbol, strategy.bearSymbol].filter(
    (ticker): ticker is string => Boolean(ticker),
  );

  return [...new Set(named)];
};

export const MULTI_FILTERS: readonly MultiFilterDefinition[] = [
  {
    // The only filter where a strategy holds more than one value, so selecting two tickers means
    // "carries either" rather than "carries both".
    //
    // Built from the three named instruments, not the old collection: that collection held whatever
    // an admin had attached, which need not have included the legs - so a strategy trading through
    // SQQQ was unfindable by it. Falls back to the collection for strategies published before the
    // back office named them.
    key: "symbol",
    valuesOf: (s) => symbolsOf(s),
    optionsOf: (all) => distinct(all, symbolsOf).sort(),
    available: true,
    growsWithSelection: true,
  },
  {
    key: "assetClass",
    reference: "asset-classes",
    valuesOf: (s, reference) => [nameOf(reference, s.assetClass)],
    optionsOf: (_, reference) => namesOf(reference),
    available: true,
  },
  {
    key: "riskProfile",
    reference: "risk-profiles",
    valuesOf: (s, reference) => [nameOf(reference, s.riskProfile)],
    optionsOf: (_, reference) => namesOf(reference),
    available: true,
  },
  {
    // A strategy that has not established its sides carries no value here, so it is filtered out by
    // any selection rather than matching all of them.
    key: "direction",
    reference: "strategy-directions",
    valuesOf: (s, reference) => [nameOf(reference, s.direction ?? 0)],
    optionsOf: (_, reference) => namesOf(reference),
    available: true,
  },
  {
    key: "timeframe",
    reference: "timeframes",
    valuesOf: (s, reference) => [nameOf(reference, s.timeframe)],
    // Shortest interval first, because that is the order the package lists them in.
    optionsOf: (_, reference) => namesOf(reference),
    available: true,
  },
];

export const BOUND_FILTERS: readonly BoundFilterDefinition[] = [
  {
    key: "minAvgAnnualProfit",
    read: (s) => statNumberByKey(s, "avgAnnualProfit"),
    satisfies: (figure, bound) => figure >= bound,
    choices: [50, 75, 100, 150],
  },
  {
    key: "minWinRate",
    read: (s) => statNumberByKey(s, "winRate"),
    satisfies: (figure, bound) => figure >= bound,
    choices: [50, 55, 60, 65],
  },
  {
    // Drawdown is published as a negative percentage, so "better than 20%" keeps everything at or
    // above -20. Written the other way round this filter silently returns the worst strategies.
    key: "maxDrawdown",
    read: (s) => statNumberByKey(s, "maxRealDrawdown"),
    satisfies: (figure, bound) => figure >= -bound,
    choices: [10, 20, 30, 40],
  },
];

export const TEXT_FILTERS: readonly TextFilterDefinition[] = [
  {
    // publicName is the only name the browse payload carries. publicStrategyName rides with a
    // subscription, and the public strategy ID is deliberately kept off the portal.
    key: "name",
    read: (s) => s.publicName,
  },
];

/** A typed term, ready to compare. Trimmed because a stray space is a typing artefact, not a term. */
function term(typed: string): string {
  return typed.trim().toLocaleLowerCase();
}

export const EMPTY_FILTER_STATE: StrategyFilterState = {
  multi: {
    assetClass: [],
    riskProfile: [],
    direction: [],
    timeframe: [],
    symbol: [],
  },
  bounds: {
    minAvgAnnualProfit: null,
    minWinRate: null,
    maxDrawdown: null,
  },
  text: {
    name: "",
  },
};

export function multiFilter(key: MultiFilterKey): MultiFilterDefinition {
  return MULTI_FILTERS.find((f) => f.key === key)!;
}

export function boundFilter(key: BoundFilterKey): BoundFilterDefinition {
  return BOUND_FILTERS.find((f) => f.key === key)!;
}

/** The filters actually carrying something. One filter counts once however many values it holds. */
export function activeFilterKeys(state: StrategyFilterState): FilterKey[] {
  return [
    ...MULTI_FILTERS.filter((f) => state.multi[f.key].length > 0).map((f) => f.key),
    ...BOUND_FILTERS.filter((f) => state.bounds[f.key] !== null).map((f) => f.key),
    ...TEXT_FILTERS.filter((f) => term(state.text[f.key]).length > 0).map((f) => f.key),
  ];
}

/**
 * Does this strategy survive the filters, optionally ignoring one of them?
 *
 * `skip` is what lets an option say how many strategies ticking it would return: count against
 * every other filter but not against its own, or every unticked option reads zero.
 */
export function matchesFilters(
  strategy: TradingStrategyDto,
  state: StrategyFilterState,
  reference: FilterReference,
  skip?: FilterKey,
): boolean {
  for (const definition of MULTI_FILTERS) {
    if (definition.key === skip) continue;

    const selected = state.multi[definition.key];
    if (selected.length === 0) continue;

    const carried = definition.valuesOf(strategy, reference[definition.key] ?? []);
    if (!carried.some((value) => selected.includes(value))) return false;
  }

  for (const definition of BOUND_FILTERS) {
    if (definition.key === skip) continue;

    const bound = state.bounds[definition.key];
    if (bound === null) continue;

    const figure = definition.read(strategy);
    // A strategy that does not carry the figure is excluded rather than treated as a zero, which
    // would place it among the genuinely poor performers. Same rule the sort comparators use.
    if (figure === null || !definition.satisfies(figure, bound)) return false;
  }

  for (const definition of TEXT_FILTERS) {
    if (definition.key === skip) continue;

    const typed = term(state.text[definition.key]);
    if (typed.length === 0) continue;

    if (!definition.read(strategy).toLocaleLowerCase().includes(typed)) return false;
  }

  return true;
}

export function applyFilters(
  strategies: TradingStrategyDto[],
  state: StrategyFilterState,
  reference: FilterReference,
  skip?: FilterKey,
): TradingStrategyDto[] {
  return strategies.filter((strategy) =>
    matchesFilters(strategy, state, reference, skip),
  );
}

/**
 * How many strategies each option of one filter would return, given everything else that is on. Filters
 * match sides, but the list shows a strategy's sides as one card, so the count is of strategies.
 */
export function optionCounts(
  strategies: TradingStrategyDto[],
  state: StrategyFilterState,
  definition: MultiFilterDefinition,
  reference: FilterReference,
): Map<string, number> {
  const counts = new Map<string, number>();
  const eligible = applyFilters(strategies, state, reference, definition.key);

  const list = reference[definition.key] ?? [];

  for (const option of definition.optionsOf(strategies, list)) {
    const matching = eligible.filter((s) => definition.valuesOf(s, list).includes(option));
    counts.set(option, countStrategies(matching));
  }

  return counts;
}

/**
 * When nothing matched, which single filter is holding the count at zero?
 *
 * Returns the one whose removal alone opens the results up the most — and null when removing any
 * one of them still leaves nothing, because there the combination is at fault and naming a filter
 * whose removal changes nothing is worse than saying so.
 */
export function tightestFilter(
  strategies: TradingStrategyDto[],
  state: StrategyFilterState,
  reference: FilterReference,
): { key: FilterKey; remaining: number } | null {
  const candidates = activeFilterKeys(state)
    .map((key) => ({
      key,
      remaining: countStrategies(applyFilters(strategies, state, reference, key)),
    }))
    .filter((candidate) => candidate.remaining > 0)
    .sort((a, b) => b.remaining - a.remaining);

  return candidates[0] ?? null;
}

export function clearFilter(
  state: StrategyFilterState,
  key: FilterKey,
): StrategyFilterState {
  if (key in state.multi) {
    return { ...state, multi: { ...state.multi, [key as MultiFilterKey]: [] } };
  }
  if (key in state.bounds) {
    return { ...state, bounds: { ...state.bounds, [key as BoundFilterKey]: null } };
  }
  return { ...state, text: { ...state.text, [key as TextFilterKey]: "" } };
}

/**
 * Read the filters out of the query string.
 *
 * Values are validated against what the catalogue actually offers, so a hand-edited or stale link
 * cannot leave a filter applied that nothing in the UI can show or clear. A single value parses the
 * same as a list, which is what keeps links made before this screen took multi-select working.
 */
export function parseFilterState(
  params: URLSearchParams,
  strategies: TradingStrategyDto[],
  reference: FilterReference,
): StrategyFilterState {
  const multi = { ...EMPTY_FILTER_STATE.multi };
  for (const definition of MULTI_FILTERS) {
    const raw = params.get(definition.key);
    if (!raw || !definition.available) continue;

    const offered = definition.optionsOf(strategies, reference[definition.key] ?? []);
    multi[definition.key] = raw
      .split(",")
      .map((value) => value.trim())
      .filter((value) => offered.includes(value));
  }

  const bounds = { ...EMPTY_FILTER_STATE.bounds };
  for (const definition of BOUND_FILTERS) {
    const raw = params.get(definition.key);
    if (raw === null) continue;

    const value = Number(raw);
    if (definition.choices.includes(value)) bounds[definition.key] = value;
  }

  // Free text is the one filter with nothing to validate against, so it round-trips as typed. A term
  // matching nothing reaches the no-results message rather than being dropped on the way in, which
  // would leave the customer looking at every strategy with an empty box and no explanation.
  const text = { ...EMPTY_FILTER_STATE.text };
  for (const definition of TEXT_FILTERS) {
    const raw = params.get(definition.key);
    if (raw !== null) text[definition.key] = raw;
  }

  return { multi, bounds, text };
}

/** Write the filters back, dropping anything not set so a clean list leaves a clean URL. */
export function writeFilterState(
  params: URLSearchParams,
  state: StrategyFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);

  for (const definition of MULTI_FILTERS) {
    const selected = state.multi[definition.key];
    if (selected.length === 0) next.delete(definition.key);
    else next.set(definition.key, selected.join(","));
  }

  for (const definition of BOUND_FILTERS) {
    const bound = state.bounds[definition.key];
    if (bound === null) next.delete(definition.key);
    else next.set(definition.key, bound.toString());
  }

  for (const definition of TEXT_FILTERS) {
    const typed = state.text[definition.key];
    if (term(typed).length === 0) next.delete(definition.key);
    else next.set(definition.key, typed.trim());
  }

  // Any filter change puts you back on the first page — the old page number describes a list that
  // no longer exists.
  next.delete("page");

  return next;
}
